/**
 * Secret scrubbing for everything leaving the process.
 *
 * pino's `redact` covers log records. It does not cover an exception's message, a
 * breadcrumb URL, or the context object handed to the reporter — so the SDK
 * boundary scrubs here, from the same list.
 *
 * Imports nothing on purpose: the browser apps use this too.
 */

/**
 * Pair every `*.x` wildcard with its root spelling. `*.x` matches exactly one
 * level down and never the root, and the root is where `createAppLogger` puts
 * caller context. Derived rather than listed twice so the two cannot drift.
 */
export function withRootVariants(paths: string[]): string[] {
  return [...new Set(paths.flatMap(p => (p.startsWith("*.") ? [p, p.slice(2)] : [p])))];
}

/**
 * Pair every `snake_case` leaf with its `camelCase` twin. A payload spells the
 * same credential either way, and listing both by hand is how `privateKey` came
 * to be covered while `apiKey` was not.
 */
export function withCamelVariants(paths: string[]): string[] {
  return [
    ...new Set(
      paths.flatMap(path => {
        const cut = path.lastIndexOf(".");
        const leaf = path.slice(cut + 1);
        if (!leaf.includes("_")) {
          return [path];
        }
        const camel = leaf.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());
        return [path, `${path.slice(0, cut + 1)}${camel}`];
      }),
    ),
  ];
}

/**
 * Every field censored before a line is written. This is the repo's single list:
 * `@repo/fastify` builds its loggers from it rather than keeping a second one,
 * which had drifted in both directions.
 *
 * Redaction addresses fields on the log *object*, so a concatenated message is
 * opaque to it — `scrub` covers that. And a wildcard reaches the root and one
 * level down, so a secret nested deeper belongs out of the object entirely.
 */
export const REDACT_PATHS = withRootVariants(
  withCamelVariants([
    // Authorization headers, in each shape a log object carries them.
    "req.headers.authorization",
    "request.headers.authorization",
    "headers.authorization",
    "req.headers.cookie",
    "res.headers['set-cookie']",
    // Credential-bearing body and payload fields. `body.refreshToken` is spelled out
    // because `*.token` is a literal key match and does not cover `refreshToken`.
    "body.refreshToken",
    "*.password",
    "*.token",
    "*.access_token",
    "*.id_token",
    "*.refresh_token",
    "*.preview_token",
    "*.api_key",
    "*.client_secret",
    "*.private_key",
    "*.privateKey",
    "*.secret",
  ]),
);

/** Kept as-is: 12 consumers already emit this, and log parsers key on it. */
export const REDACT_CENSOR = "[REDACTED]";

/** The pino `redact` block, so the three logger factories configure it identically. */
export const REDACT_OPTIONS = { paths: REDACT_PATHS, censor: REDACT_CENSOR };

const CIRCULAR = "[Circular]";

/**
 * The leaf key of every redact path. Feeds the `key=value` text pattern below —
 * not a field-name check, because Sentry's own server-side scrubbing already
 * censors secret-named fields (verified: it filters `password`, `token`,
 * `api_key`, `authorization` and the rest on arrival).
 */
const SECRET_KEYS = new Set(
  REDACT_PATHS.map(path =>
    path
      .split(".")
      .pop()!
      .replace(/\[|\]|'|"/g, "")
      .toLowerCase(),
  ),
);

/** Secret in text but not as a field: `{ code: 500 }` is not a credential. */
const TEXT_ONLY_SECRET_PARAMS = ["code"];

/**
 * The one field name Sentry's server-side scrubbing does *not* cover — verified
 * by sending each name and reading back what was stored. A cookie is a session
 * token, so it is worth the only field check we keep.
 */
const UNCOVERED_SECRET_FIELDS = new Set(["cookie", "set-cookie", "setcookie"]);

/**
 * Every key goes into a regex unescaped, so they must stay plain identifiers.
 * Asserted in `scrub.test.ts` rather than defended against here.
 */
export const SECRET_KEY_NAMES = [...SECRET_KEYS, ...TEXT_ONLY_SECRET_PARAMS];

/**
 * `key=value` anywhere in text — a query string or a sentence.
 *
 * An opaque token matches no shape, so the parameter name is the only handle.
 * `\b` keeps `error_code=500` out of it: there is no word boundary inside
 * `error_code`.
 */
const KEY_VALUE_SECRET_RE = new RegExp(
  String.raw`\b(` + SECRET_KEY_NAMES.join("|") + String.raw`)=[^\s&"';]+`,
  "gi",
);

const PATTERNS: [RegExp, string | ((match: string, ...rest: string[]) => string)][] = [
  [/\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi, REDACT_CENSOR],
  [/\b(sk|rk)_(test|live)_[A-Za-z0-9]{16,}\b/g, REDACT_CENSOR],
  [/\bsps_(live|test)_[a-z2-7]{32}\b/gi, REDACT_CENSOR],
  [/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, REDACT_CENSOR],
  [KEY_VALUE_SECRET_RE, (_match: string, key: string) => `${key}=${REDACT_CENSOR}`],
  [/\b(?:\d[ -]?){13,19}\b/g, (match: string) => (luhnValid(match) ? REDACT_CENSOR : match)],
];

/** Only censor digit runs that really are card numbers; an order id is not. */
function luhnValid(candidate: string): boolean {
  const digits = candidate.replace(/\D/g, "");
  if (digits.length < 13 || digits.length > 19) {
    return false;
  }
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits.charCodeAt(i) - 48;
    if (double) {
      n *= 2;
      if (n > 9) {
        n -= 9;
      }
    }
    sum += n;
    double = !double;
  }
  return sum % 10 === 0;
}

function scrubString(value: string): string {
  return PATTERNS.reduce<string>(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement as string),
    value,
  );
}

/**
 * Censor secret-shaped strings at any depth, plus the cookie fields Sentry does
 * not cover. Field names in general are Sentry's job, not ours.
 *
 * `seen` holds the current path, so a true cycle is marked and a reference shared
 * by two siblings is still expanded. A Node request object is circular, and this
 * runs on the error path, which must not throw.
 */
export function scrub(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (typeof value === "string") {
    return scrubString(value);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (seen.has(value)) {
    return CIRCULAR;
  }
  seen.add(value);

  let out: unknown;
  if (value instanceof Error) {
    out = scrubError(value, seen);
  } else if (Array.isArray(value)) {
    // Not `map(scrub)`: map would pass the index as `seen`.
    out = value.map(item => scrub(item, seen));
  } else {
    out = scrubFields(value, seen);
  }

  seen.delete(value);
  return out;
}

function scrubField(key: string, item: unknown, seen: WeakSet<object>): unknown {
  return UNCOVERED_SECRET_FIELDS.has(key.toLowerCase()) ? REDACT_CENSOR : scrub(item, seen);
}

function scrubFields(value: object, seen: WeakSet<object>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    result[key] = scrubField(key, item, seen);
  }
  return result;
}

function defineHidden(target: object, key: string, value: unknown): void {
  Object.defineProperty(target, key, {
    value,
    writable: true,
    configurable: true,
    enumerable: false,
  });
}

function scrubError(error: Error, seen: WeakSet<object>): Error {
  const copy = Object.create(Object.getPrototypeOf(error) as object) as Error;
  for (const key in error) {
    Object.defineProperty(copy, key, {
      value: scrubField(key, (error as unknown as Record<string, unknown>)[key], seen),
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
  defineHidden(copy, "message", scrubString(error.message));
  if (typeof error.stack === "string") {
    defineHidden(copy, "stack", scrubString(error.stack));
  }
  if (Object.hasOwn(error, "name")) {
    defineHidden(copy, "name", scrub(error.name, seen));
  }
  if (Object.hasOwn(error, "cause")) {
    defineHidden(copy, "cause", scrub(error.cause, seen));
  }
  if (error instanceof AggregateError) {
    defineHidden(copy, "errors", scrub(error.errors, seen));
  }
  return copy;
}

/** Ready-made `beforeSend` / `beforeSendLog`, so no caller writes the lambda. */
export function scrubEvent<T>(event: T): T {
  return scrub(event) as T;
}
